import {createSlice, current} from '@reduxjs/toolkit';
import omit from 'lodash.omit';
import {createSelector} from 'reselect';

import type {SelectionType} from '@pb/kit-component-selection';
import type {PayloadAction} from '@reduxjs/toolkit';
import type {AppState} from 'src/store/typedef';

type SelectionState = {
    allIds: number[] | string[],
    selection: SelectionType,
    selectTopStatus: boolean,
    lastCheckedIndex?: number,
};

type InitialState = {
    selections: {[key: string]: SelectionState},
};

export const initialState: InitialState = {
    selections: {},
};

export const dataTableSelectionSlice = createSlice({
    name: 'dataTableSelection',
    initialState,
    reducers: {
        setSearchedIds: (state, action: PayloadAction<{key: string, allIds: number[] | string[]}>) => {
            const {key, allIds} = action.payload;
            const {selections} = current(state);

            if (!selections[key]) {
                state.selections[key] = {
                    allIds,
                    selection: {ids: [], inverted: false},
                    selectTopStatus: false,
                };
            } else {
                state.selections[key].allIds = allIds;
            }
        },
        setSelection: (state, action: PayloadAction<{key: string, selection: SelectionType}>) => {
            const {key, selection} = action.payload;
            const {selections} = current(state);

            if (!selections[key]) {
                state.selections[key] = {
                    allIds: [],
                    selection,
                    selectTopStatus: false,
                };
            } else {
                state.selections[key].selection = selection;
            }
        },
        setLastCheckedIndex: (state, action: PayloadAction<{key: string, lastCheckedIndex: number}>) => {
            const {key, lastCheckedIndex} = action.payload;
            const {selections} = current(state);

            if (!selections[key]) {
                state.selections[key] = {
                    allIds: [],
                    selection: {inverted: false, ids: []},
                    selectTopStatus: false,
                    lastCheckedIndex,
                };
            } else {
                state.selections[key].lastCheckedIndex = lastCheckedIndex;
            }
        },
        clearSelection: (state, action: PayloadAction<string>) => {
            const selections = current(state).selections;
            state.selections = omit(selections, action.payload);
        },
    },
});

type SelectDataTableSelection = (state: AppState, key: string) => SelectionType;
export const selectDataTableSelection: SelectDataTableSelection = (state, key) =>
    state.selection.selections[key]?.selection || {inverted: false, ids: []};

type SelectDataTableLastCheckedIndex = (state: AppState, key: string) => number | undefined;
export const selectDataTableLastCheckedIndex: SelectDataTableLastCheckedIndex = (state, key) =>
    state.selection.selections[key]?.lastCheckedIndex;

type SelectDataTableAllIds = (state: AppState, key: string) => number[] | string[];
export const selectDataTableAllIds: SelectDataTableAllIds = (state, key) =>
    state.selection.selections[key]?.allIds || [];

export const getIdsSelector = (
    key: string,
) => (
    createSelector([
        (state: AppState) => selectDataTableSelection(state, key),
        (state: AppState) => selectDataTableAllIds(state, key),
    ], (selection, allIds) => {
        if (selection.inverted) {
            return allIds.filter((id: string | number) => !selection.ids.includes(id));
        } else {
            return selection.ids;
        }
    })
);

export const {
    setSelection,
    clearSelection,
    setSearchedIds,
    setLastCheckedIndex,
} = dataTableSelectionSlice.actions;
